import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { useApp } from "../../context/AppContext";
import adminApi from "../../lib/adminApi";
import { fileUrl } from "../../lib/api";
import { ADMIN } from "../../lib/adminI18n";
import { Loader2, Send, RefreshCw } from "lucide-react";

export default function ChatTab() {
  const { lang } = useApp();
  const a = ADMIN[lang];
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const bottomRef = useRef(null);

  const loadThreads = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminApi.threads();
      setThreads(Array.isArray(data) ? data : data?.threads || []);
    } catch (e) { toast.error(a.error); }
    finally { setLoading(false); }
  }, [a.error]);

  const loadMessages = useCallback(async (thread) => {
    if (!thread) return;
    try {
      const params = {};
      if (thread.thread_id) params.thread_id = thread.thread_id;
      if (thread.thread_type) params.thread_type = thread.thread_type;
      if (thread.guest_id) params.guest_id = thread.guest_id;
      if (thread.order_id) params.order_id = thread.order_id;
      const data = await adminApi.messages(params);
      setMessages(Array.isArray(data) ? data : data?.messages || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadThreads(); }, [loadThreads]);

  useEffect(() => {
    if (!active) return;
    loadMessages(active);
    const id = setInterval(() => loadMessages(active), 5000);
    return () => clearInterval(id);
  }, [active, loadMessages]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async () => {
    const val = text.trim();
    if (!val || !active) return;
    setText("");
    try {
      await adminApi.reply({
        thread_type: active.thread_type || "general",
        guest_id: active.guest_id || null,
        order_id: active.order_id || null,
        text: val,
        display_name: "Hämta Allt",
      });
      loadMessages(active);
    } catch (e) { toast.error(e?.response?.data?.detail || a.error); }
  };

  return (
    <div data-testid="admin-chat" className="grid gap-4 md:grid-cols-[20rem_1fr] h-[calc(100vh-13rem)]">
      <div className="rounded-2xl border border-border bg-card flex flex-col overflow-hidden">
        <div className="p-3 border-b border-border flex items-center justify-between">
          <span className="font-head font-bold text-sm">{a.threads}</span>
          <button onClick={loadThreads} className="h-8 w-8 rounded-lg hover:bg-accent grid place-items-center"><RefreshCw className="h-4 w-4" /></button>
        </div>
        <div className="flex-1 overflow-y-auto no-scrollbar">
          {loading ? (
            <div className="grid place-items-center py-10 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : threads.length === 0 ? (
            <div className="text-sm text-muted-foreground py-8 text-center px-3">{a.noThreads}</div>
          ) : threads.map((th, i) => {
            const id = th.thread_id || th.guest_id || i;
            const isActive = active && (active.thread_id === th.thread_id && active.guest_id === th.guest_id);
            return (
              <button key={id} data-testid={`admin-thread-${id}`} onClick={() => setActive(th)} className={`w-full text-start p-3 border-b border-border hover:bg-accent transition-colors ${isActive ? "bg-accent" : ""}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="font-semibold text-sm truncate">{th.display_name || th.guest_id || th.thread_type || "—"}</span>
                  {th.unread > 0 && <span className="h-5 min-w-5 px-1 rounded-full bg-destructive text-destructive-foreground text-[11px] font-bold grid place-items-center">{th.unread}</span>}
                </div>
                <div className="text-xs text-muted-foreground truncate">{th.last_message || th.thread_type || ""}</div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-card flex flex-col overflow-hidden">
        {!active ? (
          <div className="flex-1 grid place-items-center text-muted-foreground text-sm">{a.selectThread}</div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto no-scrollbar p-4 space-y-3 bg-background">
              {messages.map((m) => {
                const mine = m.sender === "admin";
                return (
                  <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[75%] rounded-2xl px-3.5 py-2 text-sm ${mine ? "bg-primary text-primary-foreground" : "bg-card border border-border"}`}>
                      {m.text}
                      {m.image_path && <img src={fileUrl(m.image_path)} alt="" className="mt-1 rounded-lg max-h-40" />}
                    </div>
                  </div>
                );
              })}
              <div ref={bottomRef} />
            </div>
            <div className="p-3 border-t border-border flex items-center gap-2">
              <input data-testid="admin-chat-input" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder={a.typeReply} className="flex-1 h-11 rounded-xl border border-border bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-primary/40" />
              <button data-testid="admin-chat-send" onClick={send} className="h-11 w-11 rounded-xl bg-primary text-primary-foreground grid place-items-center"><Send className="h-4 w-4 rtl:rotate-180" /></button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
